import { createContext, useContext, ReactNode } from 'react'

// ==========================================
// RadioGroup
// ==========================================

interface RadioGroupContextType {
    name: string
    value: string
    onChange: (value: string) => void
    disabled?: boolean
}

const RadioGroupContext = createContext<RadioGroupContextType | null>(null)

interface RadioGroupProps {
    value: string
    onChange: (value: string) => void
    name: string
    label?: string
    disabled?: boolean
    orientation?: 'vertical' | 'horizontal'
    children: ReactNode
    className?: string
}

export const RadioGroup = ({ value, onChange, name, label, disabled, orientation = 'vertical', children, className = '' }: RadioGroupProps) => {
    return (
        <RadioGroupContext.Provider value={{ name, value, onChange, disabled }}>
            <fieldset className={className} disabled={disabled}>
                {label && <legend className="mb-2 text-sm font-medium text-slate-900 dark:text-slate-100">{label}</legend>}
                <div className={`flex ${orientation === 'vertical' ? 'flex-col gap-2' : 'flex-wrap gap-4'}`}>{children}</div>
            </fieldset>
        </RadioGroupContext.Provider>
    )
}

// ==========================================
// Radio
// ==========================================

interface RadioProps {
    value: string
    label: ReactNode
    disabled?: boolean
    className?: string
}

export const Radio = ({ value, label, disabled, className = '' }: RadioProps) => {
    const ctx = useContext(RadioGroupContext)
    if (!ctx) throw new Error('Radio must be used within RadioGroup')

    const isDisabled = disabled || ctx.disabled
    const isChecked = ctx.value === value

    return (
        <label className={`inline-flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${className}`}>
            <input
                type="radio"
                name={ctx.name}
                value={value}
                checked={isChecked}
                onChange={() => ctx.onChange(value)}
                disabled={isDisabled}
                className="h-4 w-4 shrink-0 border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-600/20 disabled:cursor-not-allowed"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
        </label>
    )
}
